import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/useAuth';
import { useTheme } from '@/hooks/use-theme';
import { useSidebar } from '@/components/ui/sidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { LogOut, Menu, Moon, Sun } from 'lucide-react';
import logoImg from '@/assets/square-compass-bw.png';

/**
 * Top navigation bar — fixed at viewport top.
 * Shows app title, user info (email + role), dark mode toggle, and sign-out button.
 * On mobile, renders a hamburger button to toggle the sidebar overlay.
 * On desktop, renders the SidebarTrigger for collapse toggle.
 */
export default function TopNav() {
  const { user, signOut } = useAuth();
  const { toggleSidebar } = useSidebar();
  const { theme, toggleTheme } = useTheme();
  const isMobile = useIsMobile();

  return (
    <header
      className="sticky top-0 z-20 flex h-12 shrink-0 items-center gap-2 border-b bg-background px-4"
      role="banner"
    >
      {isMobile ? (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={toggleSidebar}
          aria-label="Open navigation menu"
        >
          <Menu className="size-4" />
        </Button>
      ) : (
        <SidebarTrigger aria-label="Toggle sidebar" />
      )}

      <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
        <img src={logoImg} alt="" aria-hidden="true" className="size-6 dark:invert" />
        <span className="text-sm font-semibold select-none">Grand Lodge of Texas - Launchpad</span>
      </Link>

      <div className="flex-1" />

      {/* Dark mode toggle */}
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={toggleTheme}
        aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
      </Button>

      {user && (
        <div className="flex items-center gap-3">
          <span className="hidden text-sm text-muted-foreground sm:inline">
            {user.email || user.username}
            <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-xs">
              {user.role}
            </span>
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => void signOut()}
            aria-label="Sign out"
          >
            <LogOut className="size-4" />
          </Button>
        </div>
      )}
    </header>
  );
}
